import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import {
  FileArrowUp, Files, Trash, Eye, CheckCircle,
  XCircle, Warning, Plus, SignOut, UserCircle, Detective
} from "@phosphor-icons/react";
import UploadDialog from "@/components/UploadDialog";
import { ROUTING_CONFIG } from "@/components/fraud/FraudScoreBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  uploaded: { label: "Uploaded", color: "bg-blue-100 text-blue-800 border-blue-200" },
  processing: { label: "Processing", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  processed: { label: "Processed", color: "bg-green-100 text-green-800 border-green-200" },
  error: { label: "Error", color: "bg-red-100 text-red-800 border-red-200" }
};

const DECISION_CONFIG = {
  approved: { label: "Approved", icon: CheckCircle, color: "text-green-700" },
  approved_with_observations: { label: "Approved*", icon: Warning, color: "text-yellow-700" },
  partial_approval: { label: "Partial", icon: Warning, color: "text-orange-700" },
  rejected: { label: "Rejected", icon: XCircle, color: "text-red-700" }
};

export default function ClaimsList({ onLogout }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const navigate = useNavigate();

  const loadingRef = useRef(true);

  const fetchClaims = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/claims`, { timeout: 15000 });
      setClaims(res.data);
    } catch (err) {
      if (loadingRef.current) {
        console.error("Failed to fetch claims:", err?.message);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []); // API is a module-level constant, no dependency needed

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const handleClaimCreated = (claimId) => {
    fetchClaims();
    navigate(`/claim/${claimId}`);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/claims/${id}`);
      toast.success("Claim deleted");
      setClaims(prev => prev.filter(c => c.id !== id));
    } catch {
      toast.error("Failed to delete claim");
    }
  };

  const formatAmount = (amt) => {
    if (!amt && amt !== 0) return "-";
    return `INR ${Number(amt).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="claims-list-page">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#002FA7] rounded-md flex items-center justify-center">
              <Files size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl tracking-tight font-semibold text-gray-900" data-testid="app-title">
                Reimbursement Product
              </h1>
              <p className="text-xs text-gray-500">AI-Powered Claim Processing</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/fraud")}
              className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="fraud-center-btn"
            >
              <Detective size={18} weight="bold" />
              Fraud Center
            </Button>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-[#002FA7] hover:bg-blue-800 text-white gap-2"
              data-testid="upload-pdf-btn"
            >
              <Plus size={18} weight="bold" />
              New Claim
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <UserCircle size={20} className="text-gray-400" />
              <span className="hidden sm:inline">{localStorage.getItem("user_name") || "User"}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="text-gray-400 hover:text-red-600"
              data-testid="logout-btn"
            >
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Claims</h2>
              <p className="text-sm text-gray-500">{claims.length} total claims</p>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-16 skeleton-shimmer rounded-md" />
              ))}
            </div>
          ) : claims.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-md p-12 text-center bg-white" data-testid="empty-state">
              <FileArrowUp size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-sm">No claims yet. Upload a PDF to get started.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">File</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Patient</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Pages</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Claimed</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Payable</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Decision</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fraud Risk</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claims.map(claim => {
                    const status = STATUS_CONFIG[claim.status] || STATUS_CONFIG.uploaded;
                    const decision = DECISION_CONFIG[claim.final_amount?.decision];
                    const DecisionIcon = decision?.icon;
                    return (
                      <TableRow
                        key={claim.id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => navigate(`/claim/${claim.id}`)}
                        data-testid={`claim-row-${claim.id}`}
                      >
                        <TableCell className="font-medium text-sm text-gray-900 max-w-[200px] truncate">
                          {claim.file_name}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {claim.patient_info?.patient_name || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${status.color} text-xs`}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{claim.total_pages}</TableCell>
                        <TableCell className="text-sm text-gray-700 font-medium">
                          {formatAmount(claim.final_amount?.claimed_amount)}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-gray-900">
                          {formatAmount(claim.final_amount?.net_payable)}
                        </TableCell>
                        <TableCell>
                          {decision ? (
                            <span className={`flex items-center gap-1 text-sm font-medium ${decision.color}`}>
                              {DecisionIcon && <DecisionIcon size={16} weight="bold" />}
                              {decision.label}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          {claim.fraud_summary ? (() => {
                            const rc = ROUTING_CONFIG[claim.fraud_summary.routing_decision] || ROUTING_CONFIG.auto_approve;
                            return (
                              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded border ${rc.bg} ${rc.border} ${rc.text}`} data-testid={`fraud-risk-${claim.id}`}>
                                {claim.fraud_summary.composite_score} · {rc.label}
                              </span>
                            );
                          })() : <span className="text-xs text-gray-300">—</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/claim/${claim.id}`); }}
                              data-testid={`view-claim-${claim.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDelete(claim.id, e)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-claim-${claim.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        onClaimCreated={handleClaimCreated}
      />
    </div>
  );
}
